import { Link, useNavigate } from "react-router-dom";
import { HardHat, LayoutDashboard, LogOut, Plus, Users, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { getRoleBadge, canManageUsers } from "@/lib/permissions";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const roleBadge = getRoleBadge(user);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="no-print sticky top-0 z-40 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 sm:px-6 h-16">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="header-logo-link">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <HardHat className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-semibold text-lg text-foreground hidden sm:inline">
              Outil SOP
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="nav-dashboard-button"
            >
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">Tableau de bord</span>
            </Button>
            {user?.permissions?.can_create && (
              <Button size="sm" onClick={() => navigate("/sops/new")} data-testid="nav-new-sop-button">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Nouvelle SOP</span>
              </Button>
            )}
            {canManageUsers(user) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/users")}
                data-testid="nav-users-button"
              >
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Utilisateurs</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/aide")}
              data-testid="nav-help-button"
              title="Aide & Tutoriel"
            >
              <HelpCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Aide</span>
            </Button>
            <div className="flex items-center gap-2 pl-2 border-l border-border">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback>{user?.name?.charAt(0) || "U"}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col leading-tight">
                <span className="text-sm text-foreground hidden md:inline">{user?.name}</span>
                {roleBadge && (
                  <Badge className={`${roleBadge.className} mt-0.5 w-fit text-[10px] py-0 whitespace-nowrap`} data-testid="user-role-badge">
                    {roleBadge.label}
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" title="Se déconnecter">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
    </div>
  );
}
